import api from './api';
import { isImageUploadFile, prepareFileForUpload } from './imageUploadCompress';

export const PHOTO_MIME_PREFIX = 'image/';
export const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
export const DOCUMENT_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'webp'];
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024;

export function createEmptyPendingUploads() {
  return {
    studentPhoto: null,
    guardianPhoto: null,
    studentAadharDoc: null,
    transferCertificate: null,
    guardianAadharDoc: null,
    otherDocuments: [],
  };
}

export function validateAadharNumber(value, label = 'Aadhar') {
  const trimmed = String(value ?? '').trim();
  if (trimmed === '') return null;
  if (!/^\d{12}$/.test(trimmed)) {
    return `${label} must be exactly 12 digits`;
  }
  return null;
}

function extOf(file) {
  const name = String(file?.name || '');
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
}

export function validatePhotoFile(file) {
  if (!file) return null;
  if (!String(file.type || '').startsWith(PHOTO_MIME_PREFIX)) {
    const ext = extOf(file);
    if (!PHOTO_EXTENSIONS.includes(ext)) {
      return 'Photo must be JPG, PNG, GIF, or WEBP';
    }
  }
  return null;
}

export function validateDocumentFile(file) {
  if (!file) return null;
  // Images are auto-compressed to ≤100KB; PDFs keep 5 MB limit.
  if (!isImageUploadFile(file) && file.size > MAX_DOCUMENT_BYTES) {
    return 'File too large. Maximum size is 5 MB.';
  }
  const ext = extOf(file);
  if (!DOCUMENT_EXTENSIONS.includes(ext)) {
    return 'Document must be PDF, JPG, PNG, or WEBP';
  }
  return null;
}

export async function uploadStudentPhoto(studentId, file) {
  const prepared = await prepareFileForUpload(file);
  const formData = new FormData();
  formData.append('photo', prepared);
  const res = await api.post(`students/${studentId}/photo`, formData);
  return res.data?.data;
}

export async function uploadGuardianPhoto(studentId, file) {
  const prepared = await prepareFileForUpload(file);
  const formData = new FormData();
  formData.append('photo', prepared);
  const res = await api.post(`students/${studentId}/guardian-photo`, formData);
  return res.data?.data;
}

export async function uploadStudentDocument(studentId, file, documentType, label = null) {
  const prepared = await prepareFileForUpload(file);
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('document_type', documentType);
  if (label) formData.append('label', label);
  const res = await api.post(`students/${studentId}/documents`, formData);
  return res.data?.data;
}

export async function deleteStudentDocument(studentId, docId) {
  await api.delete(`students/${studentId}/documents/${docId}`);
}

export async function uploadAllAdmissionAssets(studentId, pending) {
  const uploaded = [];
  const errors = [];

  const tryUpload = async (field, fn) => {
    try {
      await fn();
      uploaded.push(field);
    } catch (err) {
      errors.push({
        field,
        message: err.response?.data?.message || err.message || 'Upload failed',
      });
    }
  };

  if (pending?.studentPhoto) {
    await tryUpload('Student photo', () => uploadStudentPhoto(studentId, pending.studentPhoto));
  }
  if (pending?.guardianPhoto) {
    await tryUpload('Guardian photo', () => uploadGuardianPhoto(studentId, pending.guardianPhoto));
  }
  if (pending?.studentAadharDoc) {
    await tryUpload('Student Aadhar document', () => uploadStudentDocument(studentId, pending.studentAadharDoc, 'student_aadhar'));
  }
  if (pending?.transferCertificate) {
    await tryUpload('Transfer certificate', () => uploadStudentDocument(studentId, pending.transferCertificate, 'transfer_certificate'));
  }
  if (pending?.guardianAadharDoc) {
    await tryUpload('Guardian Aadhar document', () => uploadStudentDocument(studentId, pending.guardianAadharDoc, 'guardian_aadhar'));
  }
  const others = pending?.otherDocuments || [];
  for (let i = 0; i < others.length; i += 1) {
    const row = others[i];
    if (!row?.file) continue;
    const label = row.label?.trim() || `Document ${i + 1}`;
    await tryUpload(label, () => uploadStudentDocument(studentId, row.file, 'other', label));
  }

  return { uploaded, errors };
}

export const DOCUMENT_TYPE_LABELS = {
  transfer_certificate: 'Transfer Certificate',
  student_aadhar: 'Student Aadhar',
  guardian_aadhar: 'Guardian Aadhar',
  other: 'Other',
};
